import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

export type QuizDraft = {
  time_limit_seconds: number | null;
  questions: Array<{
    text: string;
    kind: 'single' | 'multi';
    options: Array<{ text: string; is_correct: boolean }>;
  }>;
};

function clampInt(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

function validateQuiz(draft: QuizDraft): string | null {
  if (!draft.questions.length) return 'Adaugă cel puțin o întrebare.';
  for (const [i, q] of draft.questions.entries()) {
    if (!q.text.trim()) return `Întrebarea #${i + 1} nu are text.`;
    if (q.options.length < 2) return `Întrebarea #${i + 1} trebuie să aibă minim 2 variante.`;
    const correct = q.options.filter((o) => o.is_correct).length;
    if (correct < 1) return `Întrebarea #${i + 1} trebuie să aibă minim un răspuns corect.`;
    if (q.kind === 'single' && correct !== 1) return `Întrebarea #${i + 1} (single) trebuie să aibă exact un răspuns corect.`;
    if (q.options.some((o) => !o.text.trim())) return `Întrebarea #${i + 1} are o variantă fără text.`;
  }
  return null;
}

interface Props {
  open: boolean;
  initial: QuizDraft | null;
  onClose: () => void;
  onSave: (draft: QuizDraft) => void;
  onSaveAndReactivate?: (draft: QuizDraft) => void;
  onClear: () => void;
  showReactivateAction?: boolean;
}

const QuizBuilderModal: React.FC<Props> = ({
  open,
  initial,
  onClose,
  onSave,
  onSaveAndReactivate,
  onClear,
  showReactivateAction = false,
}) => {
  const makeEmptyDraft = (): QuizDraft => ({
    time_limit_seconds: null,
    questions: [
      {
        text: '',
        kind: 'single',
        options: [
          { text: '', is_correct: true },
          { text: '', is_correct: false },
        ],
      },
    ],
  });

  const [timeMinutesRaw, setTimeMinutesRaw] = useState<string>('');
  const [draft, setDraft] = useState<QuizDraft>(() => initial ?? makeEmptyDraft());

  const validationError = useMemo(() => validateQuiz(draft), [draft]);
  useEffect(() => {
    if (!open) return;
    const next = initial ?? makeEmptyDraft();
    setDraft(next);
    const sec = next.time_limit_seconds ?? null;
    setTimeMinutesRaw(sec ? String(Math.round(sec / 60)) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  if (!open) return null;

  const setQuestion = (idx: number, patch: Partial<QuizDraft['questions'][number]>) => {
    setDraft((prev) => {
      const next = prev.questions.slice();
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, questions: next };
    });
  };

  const setOption = (qIdx: number, oIdx: number, patch: Partial<QuizDraft['questions'][number]['options'][number]>) => {
    setDraft((prev) => {
      const questions = prev.questions.slice();
      const q = questions[qIdx];
      const options = q.options.slice();
      options[oIdx] = { ...options[oIdx], ...patch };

      // If single-choice and we set a correct option, unset others.
      if (q.kind === 'single' && patch.is_correct) {
        for (let i = 0; i < options.length; i++) {
          if (i !== oIdx) options[i] = { ...options[i], is_correct: false };
        }
      }

      questions[qIdx] = { ...q, options };
      return { ...prev, questions };
    });
  };

  const addQuestion = () => {
    setDraft((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          text: '',
          kind: 'single',
          options: [
            { text: '', is_correct: true },
            { text: '', is_correct: false },
          ],
        },
      ],
    }));
  };

  const removeQuestion = (idx: number) => {
    setDraft((prev) => ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }));
  };

  const addOption = (qIdx: number) => {
    setDraft((prev) => {
      const questions = prev.questions.slice();
      const q = questions[qIdx];
      questions[qIdx] = { ...q, options: [...q.options, { text: '', is_correct: false }] };
      return { ...prev, questions };
    });
  };

  const removeOption = (qIdx: number, oIdx: number) => {
    setDraft((prev) => {
      const questions = prev.questions.slice();
      const q = questions[qIdx];
      const options = q.options.filter((_, i) => i !== oIdx);
      questions[qIdx] = { ...q, options };
      return { ...prev, questions };
    });
  };

  const buildNextDraft = (): QuizDraft | null => {
    const minutes = timeMinutesRaw.trim() ? Number(timeMinutesRaw.trim()) : NaN;
    const time_limit_seconds =
      Number.isFinite(minutes) && minutes > 0 ? clampInt(minutes, 1, 180) * 60 : null;

    const next: QuizDraft = { ...draft, time_limit_seconds };
    const err = validateQuiz(next);
    if (err) {
      window.alert(err);
      return null;
    }
    return next;
  };

  const handleSave = () => {
    const next = buildNextDraft();
    if (!next) return;
    onSave(next);
    onClose();
  };

  const handleSaveAndReactivate = () => {
    if (!onSaveAndReactivate) return;
    const next = buildNextDraft();
    if (!next) return;
    if (
      !window.confirm(
        'Reactivarea va reseta toate rezolvarile existente. Elevii vor putea rezolva din nou quizul. Continui?'
      )
    ) {
      return;
    }
    onSaveAndReactivate(next);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-3xl bg-card border border-border rounded-xl p-5 max-h-[85vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <div className="text-lg font-semibold text-foreground">Quiz</div>
            <div className="text-xs text-muted-foreground mt-1">
              Întrebări cu răspuns unic sau multiplu (autocorectare).
            </div>
          </div>
          <button
            className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
            onClick={onClose}
            aria-label="Închide"
            type="button"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="mb-4">
          <Input
            label="Timp de lucru (minute, optional)"
            value={timeMinutesRaw}
            onChange={(e) => setTimeMinutesRaw(e.target.value)}
            placeholder="ex: 10"
          />
        </div>

        <div className="space-y-5">
          {draft.questions.map((q, qIdx) => (
            <div key={qIdx} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <Input
                    label={`Întrebarea #${qIdx + 1}`}
                    value={q.text}
                    onChange={(e) => setQuestion(qIdx, { text: e.target.value })}
                    placeholder="Scrie întrebarea..."
                  />
                </div>
                <div className="shrink-0">
                  <label className="block text-sm font-medium text-foreground mb-1">Tip</label>
                  <select
                    value={q.kind}
                    onChange={(e) => {
                      const kind = (e.target.value === 'multi' ? 'multi' : 'single') as 'single' | 'multi';
                      // If switching to single, keep only first correct.
                      if (kind === 'single') {
                        const firstCorrect = q.options.findIndex((o) => o.is_correct);
                        const keepIdx = firstCorrect >= 0 ? firstCorrect : 0;
                        const opts = q.options.map((o, i) => ({ ...o, is_correct: i === keepIdx }));
                        setQuestion(qIdx, { kind, options: opts });
                      } else {
                        setQuestion(qIdx, { kind });
                      }
                    }}
                    className="min-w-[140px] px-3 pr-9 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="single">Un răspuns</option>
                    <option value="multi">Mai multe</option>
                  </select>
                </div>
              </div>

              <div className="mt-3 space-y-2">
                <div className="text-sm font-medium text-foreground">Variante</div>
                {q.options.map((o, oIdx) => (
                  <div key={oIdx} className="flex items-center gap-2">
                    <input
                      type={q.kind === 'single' ? 'radio' : 'checkbox'}
                      checked={o.is_correct}
                      onChange={(e) => setOption(qIdx, oIdx, { is_correct: e.target.checked })}
                      name={`q-${qIdx}-correct`}
                      className="h-4 w-4"
                    />
                    <input
                      value={o.text}
                      onChange={(e) => setOption(qIdx, oIdx, { text: e.target.value })}
                      placeholder={`Varianta #${oIdx + 1}`}
                      className="flex-1 px-3 py-2 text-sm bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(qIdx, oIdx)}
                      className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
                      aria-label="Șterge variantă"
                      disabled={q.options.length <= 2}
                    >
                      <Icon name="Trash2" size={16} className={q.options.length <= 2 ? 'opacity-30' : ''} />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <Button type="button" variant="outline" size="sm" onClick={() => addOption(qIdx)} iconName="Plus">
                    Adaugă variantă
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeQuestion(qIdx)}
                    iconName="Trash2"
                    disabled={draft.questions.length <= 1}
                  >
                    Șterge întrebarea
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button type="button" variant="outline" onClick={addQuestion} iconName="Plus">
            Adaugă întrebare
          </Button>

          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={onClear} disabled={!initial}>
              Șterge quiz
            </Button>
            {showReactivateAction && onSaveAndReactivate ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveAndReactivate}
                disabled={Boolean(validationError)}
              >
                Salvează + reactivează
              </Button>
            ) : null}
            <Button type="button" variant="default" onClick={handleSave} disabled={Boolean(validationError)}>
              Salvează quiz
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizBuilderModal;
